import { useState, useMemo } from 'react';
import { IntegratedTaskGroup, TaskDetailModal, ResolveIssuesModal } from '../components/tasks';
import { TRANSLATIONS } from '../config/translations';

/**
 * Dashboard view - shows task statistics and grouped tasks
 */
export function DashboardView({ currentUser, language, tasks, locations, users, loading }) {
  // PHASE 3 OPTIMIZATION: Receive data via props (no duplicate listeners)
  const [selectedTask, setSelectedTask] = useState(null);
  const [resolvingTask, setResolvingTask] = useState(null);
  const t = TRANSLATIONS[language];

  // PHASE 3: Listeners removed - data comes from App component props

  const today = new Date().toISOString().split('T')[0];

  // Filter tasks for non-admin users (only show their assigned tasks)
  const userTasks = useMemo(() => {
    if (currentUser.role === 'admin') {
      return tasks;
    }
    return tasks.filter(task => task.assigneeId === currentUser.uid);
  }, [tasks, currentUser]);

  const groupedTasks = useMemo(() => {
    const overdue = userTasks.filter(t => t.dueDate < today && (t.status === 'open' || t.status === 'overdue'))
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)); // Sort by due date (earliest first)

    const todayTasks = userTasks.filter(t => t.dueDate === today && (t.status === 'open' || t.status === 'overdue'))
      .sort((a, b) => (a.title || '').localeCompare(b.title || '')); // Sort alphabetically by title

    const upcoming = userTasks.filter(t => t.dueDate > today && (t.status === 'open' || t.status === 'overdue'))
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)); // Sort by due date (earliest first)

    const completed = userTasks.filter(t => t.status === 'completed' || t.status === 'completed-with-issues')
      .sort((a, b) => {
        // Sort completed-with-issues to top
        if (a.status === 'completed-with-issues' && b.status !== 'completed-with-issues') return -1;
        if (a.status !== 'completed-with-issues' && b.status === 'completed-with-issues') return 1;
        // Then sort by completion date (newest first)
        return b.completedAt?.seconds - a.completedAt?.seconds;
      });

    return { overdue, today: todayTasks, upcoming, completed };
  }, [userTasks, today]);

  const stats = {
    overdue: groupedTasks.overdue.length,
    today: groupedTasks.today.length,
    upcoming: groupedTasks.upcoming.length,
    completed: groupedTasks.completed.length
  };

  // All tasks are shown — groups scroll internally instead of truncating.
  // (Previously getTaskLimit cut groups off with "... und N weitere".)

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="dashboard-view">
      <div className="view-header">
        <h2>Dashboard</h2>
        <p className="welcome-text">Willkommen zurück, {currentUser.displayName}!</p>
      </div>

      <div className="dashboard-grid">
        {/* Overdue Group */}
        <IntegratedTaskGroup
          groupName="overdue"
          title="Überfällig"
          icon="fas fa-exclamation-circle"
          count={stats.overdue}
          tasks={groupedTasks.overdue}
          locations={locations}
          users={users}
          onSelectTask={setSelectedTask}
          variant="cards"
          colorClass="stat-danger"
        />

        {/* Today Group */}
        <IntegratedTaskGroup
          groupName="today"
          title="Heute fällig"
          icon="fas fa-calendar-day"
          count={stats.today}
          tasks={groupedTasks.today}
          locations={locations}
          users={users}
          onSelectTask={setSelectedTask}
          variant="cards"
          colorClass="stat-warning"
        />

        {/* Upcoming Group */}
        <IntegratedTaskGroup
          groupName="upcoming"
          title="Anstehend"
          icon="fas fa-clock"
          count={stats.upcoming}
          tasks={groupedTasks.upcoming}
          locations={locations}
          users={users}
          onSelectTask={setSelectedTask}
          variant="cards"
          colorClass="stat-info"
        />

        {/* Completed Group */}
        <IntegratedTaskGroup
          groupName="completed"
          title="Erledigt"
          icon="fas fa-check-circle"
          count={stats.completed}
          tasks={groupedTasks.completed}
          locations={locations}
          users={users}
          onSelectTask={setSelectedTask}
          variant="compact"
          colorClass="stat-success"
        />
      </div>

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          currentUser={currentUser}
          locations={locations}
          users={users}
          onClose={() => setSelectedTask(null)}
          onComplete={() => {
            // Firestore listener will automatically update UI
            setSelectedTask(null);
          }}
          onResolve={currentUser.role === 'admin' && selectedTask.status === 'completed-with-issues'
            ? () => {
                setResolvingTask(selectedTask);
                setSelectedTask(null);
              }
            : null}
          language={language}
        />
      )}
      
      {resolvingTask && (
        <ResolveIssuesModal
          task={resolvingTask}
          onClose={() => setResolvingTask(null)}
          onSuccess={() => {
            setResolvingTask(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// INTEGRATED TASK GROUP - Stats + Collapsible Tasks Combined
// ============================================================================

